import { debounce, throttle } from '@modules/helpers';
import { getScrollableParent } from '@modules/screen-helpers';
import { useEffect, useRef, useState } from 'react';

interface UseScrollBasedPinOptions {
  pinThreshold?: number;
  downScrollTolerance?: number;
  upScrollTolerance?: number;
  targetNode?: HTMLElement | SVGElement;
  onExceedThreshold?: 'pin' | 'unpin';
}

/**
 * Returns whether or not a component should be pinned depending on the scroll position
 * @param initialState Control the very first state emitted
 * @param options Fine tune when the object is pinned or not
 */
export default function useScrollBasedPin(
  initialState: boolean,
  options?: UseScrollBasedPinOptions,
): boolean {
  const [isPinned, setIsPinned] = useState(initialState);
  const scrollRecord = useRef(0);

  const {
    pinThreshold = 0,
    downScrollTolerance = 2,
    upScrollTolerance = 5,
    targetNode,
    onExceedThreshold = 'unpin',
  } = options || {};

  useEffect(() => {
    const scrollableContainer = targetNode ? getScrollableParent(targetNode) : window;
    if (!scrollableContainer) return;

    const isOutOfBounds = (scrollY: number) => {
      const viewportHeight = getViewportHeight(scrollableContainer);
      const scrollableHeight = getScrollableHeight(scrollableContainer);

      const pastTop = scrollY < 0;
      const pastBottom = scrollY + viewportHeight > scrollableHeight;

      return pastTop || pastBottom;
    };

    const getPinState = (scrollY: number) => {
      const scrollDirection = scrollY >= scrollRecord.current ? 'down' : 'up';
      const distanceScrolled = Math.abs(scrollY - scrollRecord.current);
      const scrollPosPastThreshold = scrollY > pinThreshold;

      if (scrollDirection === 'down' && scrollPosPastThreshold && distanceScrolled > downScrollTolerance) {
        return onExceedThreshold;
      } else if (
        (scrollDirection === 'up' && distanceScrolled > upScrollTolerance) ||
        !scrollPosPastThreshold
      ) {
        return getOpposite(onExceedThreshold);
      } else {
        return 'constant';
      }
    };

    const handleScroll = () => {
      const scrollY = getDistanceFromTop(scrollableContainer);

      if (!isOutOfBounds(scrollY)) {
        const action = getPinState(scrollY);

        if (action === 'pin' && !isPinned) setIsPinned(true);
        if (action === 'unpin' && isPinned) setIsPinned(false);
      }
      scrollRecord.current = scrollY <= 0 ? 0 : scrollY; // For Mobile or negative scrolling
    };

    // By throttling the scroll event we introduce the possibility of missing a key scroll point that the user
    // may rest on so we also debounce.
    const throttledResize = throttle(handleScroll, 100);
    const debouncedScroll = debounce(handleScroll, 100);

    scrollableContainer.addEventListener('scroll', throttledResize);
    scrollableContainer.addEventListener('scroll', debouncedScroll);
    return () => {
      scrollableContainer.removeEventListener('scroll', throttledResize);
      scrollableContainer.removeEventListener('scroll', debouncedScroll);
    };
  }, [isPinned, downScrollTolerance, pinThreshold, upScrollTolerance, targetNode, onExceedThreshold]);

  return isPinned;
}

function getOpposite(action: 'pin' | 'unpin') {
  switch (action) {
    case 'pin':
      return 'unpin';
    case 'unpin':
      return 'pin';
  }
}

function getDistanceFromTop(el: HTMLElement | Window): number {
  if (el instanceof Window) {
    return el.scrollY;
  }
  return el.scrollTop;
}

function getViewportHeight(el: HTMLElement | Window): number {
  if (el instanceof Window) {
    return el.innerHeight;
  }
  return el.clientHeight;
}

function getScrollableHeight(el: HTMLElement | Window): number {
  if (el instanceof Window) {
    return el.innerHeight;
  }
  return Math.max(el.scrollHeight, el.offsetHeight, el.clientHeight);
}
